import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete using Photon (komoot) - free, no API key.
 * Suggestions appear after 3+ characters, debounced 300ms.
 */
const AddressAutocomplete = ({ value, onChange, placeholder = "Adresse", lang = "fr" }) => {
  const [input, setInput] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const tRef = useRef(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setInput(v);
    onChange?.(v);
    if (tRef.current) clearTimeout(tRef.current);
    if (v.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    tRef.current = setTimeout(async () => {
      try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(v)}&lang=${lang}&limit=6`;
        const r = await fetch(url);
        const data = await r.json();
        const feats = (data?.features || []).map((f) => {
          const p = f.properties || {};
          const parts = [
            [p.housenumber, p.street].filter(Boolean).join(" "),
            p.postcode,
            p.city || p.town || p.village,
            p.country,
          ].filter(Boolean);
          return { label: parts.join(", "), name: p.name };
        }).filter((x) => x.label);
        setResults(feats);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const pick = (label) => {
    setInput(label);
    onChange?.(label);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={input}
          onChange={handleChange}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
          autoComplete="off"
        />
        {loading && <Loader2 className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 shadow-xl max-h-72 overflow-y-auto">
          {results.map((r, i) => (
            <li
              key={i}
              onClick={() => pick(r.label)}
              className="px-3 py-2 text-sm text-slate-700 hover:bg-[#007EAE]/10 cursor-pointer flex items-start gap-2"
            >
              <MapPin className="w-4 h-4 text-[#007EAE] mt-0.5 shrink-0" />
              <span>{r.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
